import {
  faArrowsRotate,
  faChevronDown,
  faChevronUp,
  faPlus,
  faTrash
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  InputLabel,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { useState } from 'react';

import { CopyToClipboard } from '@/components/CopyToClipboard';
import LargeCard from '@/components/LargeCard';
import useConfirm from '@/hooks/useConfirm';
import getDateTime from '@/utils/getDateTime.utils';

import {
  formatRoleLabel,
  useDeleteAuthKey,
  useFetchAuthKeys,
  useMutationAuthKeys,
  useUpdateAuthKey
} from './UserAuthKeys.store';
import useStyles from './UserAuthKeys.styles';
import type { UserAuthKeysProps } from './UserAuthKeys.types';

const RoleChips = ({ roles }: { roles?: string[] | null }) => (
  <>
    {roles?.map((role) => (
      <Chip key={role} label={formatRoleLabel(role)} size="small" />
    ))}
  </>
);

const AuthKeyDisplay = ({
  clientId,
  clientSecret
}: {
  clientId: string;
  clientSecret: string;
}) => {
  const { classes } = useStyles();

  return (
    <>
      <Box>
        <Typography sx={{ fontWeight: 'bold' }}>Client ID</Typography>
        <Paper className={classes.copyTextItem} variant="outlined">
          {clientId}
          <CopyToClipboard text={clientId} />
        </Paper>
      </Box>
      <Box>
        <Typography sx={{ fontWeight: 'bold' }}>Client Secret</Typography>
        <Paper className={classes.copyTextItem} variant="outlined">
          {clientSecret}
          <CopyToClipboard text={clientSecret} />
        </Paper>
      </Box>
    </>
  );
};

const UserAuthKeys = ({ userId }: UserAuthKeysProps) => {
  const { classes } = useStyles();
  const [openDetails, setopenDetails] = useState<number[]>([]);
  const { data: authKeys, refetch } = useFetchAuthKeys(userId);
  const { mutate: createAuthKey, data: newAuthKey } = useMutationAuthKeys(
    refetch,
    userId
  );
  const deleteAuthKey = useDeleteAuthKey(refetch, userId);
  const { mutate: updateAuthKey, data: updatedAuthKey } = useUpdateAuthKey(
    refetch,
    userId
  );

  const handleDetailsClick = (index: number) => {
    if (openDetails.includes(index)) {
      setopenDetails(openDetails.filter((key) => key !== index));
    } else {
      setopenDetails([...openDetails, index]);
    }
  };

  const latestKey = updatedAuthKey ?? newAuthKey;
  const file = latestKey
    ? new Blob(
        [
          `Client ID: ${latestKey.data.client_id}\nClient Secret: ${latestKey.data.client_secret}`
        ],
        { type: 'text/plain' }
      )
    : null;

  const { ConfirmationModal: KeyDeleteModal, confirm: confirmDelete } =
    useConfirm({
      title: 'Delete Auth Key',
      message: 'Please confirm that you want to delete this auth key.',
      button: 'Delete'
    });

  const handleDelete = async (keyId: number) => {
    const confirmed = await confirmDelete();

    if (confirmed && keyId) {
      deleteAuthKey.mutate(keyId);
    }
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.download = 'Pondurance_Scope_Auth_Key';
    link.href = file ? URL.createObjectURL(file) : '';
    link.click();
  };

  return (
    <LargeCard
      title="User Auth Keys"
      rightContent={
        <Button
          onClick={() => createAuthKey()}
          startIcon={<FontAwesomeIcon icon={faPlus} />}
        >
          New Key
        </Button>
      }
    >
      <Stack spacing={2}>
        {authKeys.length === 0 && !newAuthKey && !updatedAuthKey && (
          <Typography sx={{ fontStyle: 'italic' }}>
            User has no auth keys.
          </Typography>
        )}
        {newAuthKey && (
          <Stack className={classes.authKeyItem}>
            <Stack
              direction="row"
              sx={{ justifyContent: 'space-between', alignItems: 'center' }}
            >
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography variant="h6">New Auth Key</Typography>
                <RoleChips roles={newAuthKey.data.roles} />
              </Stack>
              <Button onClick={handleDownload} variant="contained">
                Download
              </Button>
            </Stack>
            <Stack spacing={2} sx={{ paddingTop: 2 }}>
              <Alert severity="warning">
                Important: Your secret key is shown only once. Please download
                it or securely store it before closing this screen.
              </Alert>
              <AuthKeyDisplay
                clientId={newAuthKey.data.client_id}
                clientSecret={newAuthKey.data.client_secret}
              />
            </Stack>
          </Stack>
        )}
        {updatedAuthKey && (
          <Stack className={classes.authKeyItem}>
            <Stack
              sx={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography variant="h6">Reset Key</Typography>
                <RoleChips roles={updatedAuthKey.data.roles} />
              </Stack>
              <Button variant="contained">
                <a
                  download="Pondurance_Scope_Auth_Key"
                  target="_blank"
                  rel="noreferrer"
                  href={file ? URL.createObjectURL(file) : ''}
                  style={{ textDecoration: 'inherit', color: 'inherit' }}
                >
                  Download
                </a>
              </Button>
            </Stack>
            <Stack sx={{ paddingTop: 2 }} spacing={2}>
              <Alert severity="warning">
                Important: Your secret key is shown only once. Please download
                it or securely store it before closing this screen.
              </Alert>
              <AuthKeyDisplay
                clientId={updatedAuthKey.data.client_id}
                clientSecret={updatedAuthKey.data.client_secret}
              />
            </Stack>
          </Stack>
        )}
        {authKeys?.map(
          (key, index) =>
            key.id !== newAuthKey?.data.id &&
            key.id !== updatedAuthKey?.data.id && (
              <div className={classes.authKeyItem} key={key.id}>
                <Stack
                  sx={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                  key={key.id}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: 'center' }}
                  >
                    <Typography variant="h6">
                      Client ID: {key.client_id}
                    </Typography>
                    <RoleChips roles={key.roles} />
                  </Stack>
                  <Stack direction="row" spacing={2}>
                    <IconButton
                      onClick={() => updateAuthKey(key.id)}
                      size="small"
                    >
                      <FontAwesomeIcon size="sm" icon={faArrowsRotate} />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDelete(key.id)}
                      size="small"
                    >
                      <FontAwesomeIcon size="sm" icon={faTrash} />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDetailsClick(index)}
                      size="small"
                    >
                      <FontAwesomeIcon
                        size="sm"
                        icon={
                          openDetails.includes(index)
                            ? faChevronUp
                            : faChevronDown
                        }
                      />
                    </IconButton>
                  </Stack>
                </Stack>
                {openDetails.includes(index) && (
                  <Collapse key={`collapse-${key.id}`} in>
                    <Stack sx={{ pt: 2 }} spacing={2}>
                      <div>
                        <InputLabel htmlFor="created_at">Created At</InputLabel>
                        <TextField
                          disabled
                          id="created_at"
                          value={getDateTime(key.created_at)}
                        />
                      </div>
                      <div>
                        <InputLabel htmlFor="last-used">Last Used</InputLabel>
                        <TextField
                          disabled
                          id="last-used"
                          value={
                            key.last_used ? getDateTime(key.last_used) : ''
                          }
                        />
                      </div>
                    </Stack>
                  </Collapse>
                )}
              </div>
            )
        )}
      </Stack>
      <KeyDeleteModal />
    </LargeCard>
  );
};

export default UserAuthKeys;
